import { computed } from "vue"

/**
 * Type definition for the event bus
 */
interface EventBus {
    $on: (event: string, callback: (...args: any[]) => void) => void
    $emit: (event: string, ...args: any[]) => void
    $off: (event: string, callback: (...args: any[]) => void) => void
}

export const useProvinceSelection = () => {
    // useCookie is isomorphic: readable on server (for SSR) and client alike.
    // It replaces the previous localStorage approach so the server can pass the
    // selected province to the API during SSR requests.
    const selectedProvince = useCookie<string>("selected-province", {
        maxAge: 60 * 60 * 24 * 30, // 30 days
        sameSite: "lax",
    })

    console.log(selectedProvince.value, "selectedProvince")

    const showProvinceModal = useState<boolean>(
        "show-province-modal",
        () => false
    )
    const pendingCartNavigation = useState<boolean>(
        "pending-cart-navigation",
        () => false
    )

    // Pending action to execute after province is saved from modal
    let pendingAction: ((province: string) => void) | null = null

    // Check if province is saved in cookie
    const isProvinceSelected = computed(() => {
        return !!selectedProvince.value && selectedProvince.value.trim() !== ""
    })

    // Check if a valid province is selected (not "None" / "Todas")
    const isValidProvinceSelected = computed(() => {
        return (
            !!selectedProvince.value &&
            selectedProvince.value.trim() !== "" &&
            selectedProvince.value !== "None" &&
            selectedProvince.value !== "Todas"
        )
    })

    // Get saved province from cookie
    const getSavedProvince = (): string | null => {
        return selectedProvince.value || null
    }

    // Emit product list update event
    const emitProductListUpdate = (skipCartRefresh: boolean = false) => {
        if (import.meta.client) {
            try {
                const nuxtApp = useNuxtApp()
                if (nuxtApp.$bus) {
                    const $bus = nuxtApp.$bus as EventBus
                    const province = selectedProvince.value
                    // Convert empty string to null for API consistency
                    const provinceValue =
                        province === "" || !province ? null : province
                    $bus.$emit("update:product-list", {
                        params: {
                            province: provinceValue,
                        },
                    })
                    // Refresh cart data unless skipped (e.g. when a pending add-to-cart will do it)
                    if (!skipCartRefresh) {
                        const shopCartStore = useShopCartStore()
                        shopCartStore.fetchShopCart()
                    }
                } else {
                    console.warn("Event bus not available")
                }
            } catch (error) {
                console.warn("Could not emit product list update:", error)
            }
        }
    }

    // Helper function to update province data
    const updateProvinceData = (
        province: string,
        shouldReloadShopList: boolean = false,
        skipCartRefresh: boolean = false
    ) => {
        // If province is "None" or "Todas", save empty string instead
        const normalizedProvince =
            province === "None" || province === "Todas" ? "" : province
        selectedProvince.value = normalizedProvince

        // Emit event to update product list when province changes (client only)
        emitProductListUpdate(skipCartRefresh)

        // Conditionally reload shoplistStore
        if (
            import.meta.client &&
            (shouldReloadShopList || normalizedProvince === "")
        ) {
            const shopListStore = useShopListStore()
            shopListStore.fetchShopList()
        }
    }

    // Set province in cookie
    const setProvince = (province: string) => {
        updateProvinceData(
            province,
            province === "None" || province === "Todas"
        )
    }

    // Clear province from cookie
    const clearProvince = () => {
        selectedProvince.value = ""
    }

    // Check if current selection is "None" option (or null/empty)
    const isNoneSelected = computed(() => {
        return (
            !selectedProvince.value ||
            selectedProvince.value === "None" ||
            selectedProvince.value === "Todas"
        )
    })

    // Check and show modal if needed
    const checkProvinceSelection = () => {
        if (!isProvinceSelected.value) {
            showProvinceModal.value = true
        }
    }

    // Open province modal triggered by cart navigation
    const openProvinceModalForCart = () => {
        pendingCartNavigation.value = true
        showProvinceModal.value = true
    }

    // Open province modal with a pending action to execute after province is saved
    const openProvinceModalWithAction = (
        action: (province: string) => void
    ) => {
        pendingAction = action
        showProvinceModal.value = true
    }

    // Handle province saved event
    const onProvinceSaved = (province: string) => {
        const hasPendingAction = !!pendingAction

        // When there's a pending add-to-cart action, skip the cart refresh
        // to avoid race conditions — the addToCart will trigger its own fetchShopCart
        updateProvinceData(province, !hasPendingAction, hasPendingAction)
        showProvinceModal.value = false

        if (pendingCartNavigation.value) {
            pendingCartNavigation.value = false
            const router = useRouter()
            router.push({ path: "/cart" })
        } else if (hasPendingAction) {
            const action = pendingAction
            pendingAction = null
            action(province)
        }
    }

    // Handle province changed event (when user updates from menu)
    const onProvinceChanged = (province: string) => {
        // For menu changes, always reload the shop list to ensure fresh data
        updateProvinceData(province, true)
    }

    // Handle modal hide event
    const onModalHide = () => {
        showProvinceModal.value = false
        pendingCartNavigation.value = false
        pendingAction = null
    }

    // No-op kept for backward compatibility – cookie initializes itself automatically
    const initializeProvince = () => {}

    return {
        showProvinceModal,
        selectedProvince,
        isProvinceSelected,
        isValidProvinceSelected,
        isNoneSelected,
        getSavedProvince,
        setProvince,
        clearProvince,
        checkProvinceSelection,
        onProvinceSaved,
        onProvinceChanged,
        onModalHide,
        initializeProvince,
        openProvinceModalForCart,
        openProvinceModalWithAction,
    }
}
